/**
 * CallTrackingMetrics Integration Module (Story 22.3)
 *
 * Barrel export + side-effect adapter registration.
 *
 * Registration happens here — NOT in `ctm.ts` — to prevent the
 * Failure 5a anti-pattern documented in `google/index.ts:7`. Importing
 * this module is sufficient to register the adapter; importing
 * `ctm.ts` directly would give you the adapter object without
 * registration.
 */

import { registerAdapter } from "../registry";
import { ctmAdapter } from "./ctm";

// Side-effect registration (Story 22.3)
registerAdapter("calltrackingmetrics", ctmAdapter);

// Public surface — adapter + canonical constants
export {
  ctmAdapter,
  CTM_PLATFORM_VALUES,
  CTM_METRICS,
  CTM_DIMENSIONS,
  CTM_API_VERSION,
  CTM_API_BASE_US,
  CTM_API_BASE_EU,
  CTM_FRESHNESS_LAG_HOURS,
  sanitizeCtmPii,
  // Story 22.5 — public KV-side-channel readers for the recording routes
  readCtmRecordingsEnabled,
  readCtmRouteMetadata,
  type CtmPlatform,
  type CtmMetadata,
} from "./ctm";

// API helpers
export {
  discoverCtmAccounts,
  validateCtmConnection,
  probeCtmConversationIntelligence,
  // Story 22.5 — recording-related upstream probes + fetchers
  probeCtmRecordingsEnabled,
  fetchCtmRecordingUrl,
  type FetchCtmRecordingUrlResult,
  fetchCtmCalls,
  fetchAllCtmCalls,
  fetchCtmFormSubmissions,
  fetchAllCtmFormSubmissions,
  fetchCtmSmsMessages,
  fetchAllCtmSmsMessages,
  // Story 35.4 — NEW endpoint helpers
  fetchCtmTriggers,
  fetchAllCtmTriggers,
  fetchCtmIntegrations,
  fetchAllCtmIntegrations,
  CTM_PER_PAGE_MAX,
  type CtmAccount,
  type DiscoverCtmAccountsResult,
  type ProbeCtmCiResult,
  type CtmCiEntitlement,
  type FetchCtmCallsParams,
  type FetchCtmFormSubmissionsParams,
  type FetchCtmSmsMessagesParams,
  type CtmCallsResponse,
  type CtmFormSubmissionsResponse,
  type CtmSmsMessagesResponse,
  // Story 35.4 — NEW endpoint param + response types
  type FetchCtmTriggersParams,
  type CtmTriggersResponse,
  type FetchCtmIntegrationsParams,
  type CtmIntegrationsResponse,
} from "./api";

// Story 35.4 — Entitlement re-probe surface (cron-callable)
export {
  refreshCtmEntitlements,
  shouldReprobe,
  buildEntitlementFlipEvent,
  CTM_ENTITLEMENT_PROBE_THROTTLE_MS,
  type RefreshCtmEntitlementsInput,
  type RefreshCtmEntitlementsOutcome,
  type EntitlementFlagOutcome,
} from "./entitlement-expansion";

// Region
export {
  CTM_REGION_VALUES,
  CTM_DEFAULT_REGION,
  baseUrlFor,
  isCtmRegion,
  type CtmRegion,
} from "./region";

// Auth
export { buildCtmBasicAuthHeader } from "./auth";

// Errors
export {
  parseCtmApiError,
  ctmErrorTypeToAdapterCode,
  sanitizeCtmErrorMessage,
  CtmApiCallError,
  CTM_PER_PAGE_ERROR_MESSAGE,
  type CtmApiError,
  type CtmErrorType,
} from "./errors";

// Safe fetch
export { safeFetchCtm, CTM_429_COOLDOWN_MS } from "./safe-fetch";

// Cache policy
export {
  CTM_ENDPOINT_CACHE_TTL,
  getCtmEndpointCacheTtl,
  buildCtmEndpointCacheKey,
  type CtmCacheableEndpoint,
} from "./cache-policy";

// Fields
export {
  CTM_FIELD_ALLOWLIST,
  CTM_FIELD_ALLOWLIST_BY_ENDPOINT,
  CTM_CI_GATED_FIELDS,
  CTM_LEAD_REACTOR_GATED_FIELDS,
  CTM_SCORECARD_GATED_FIELDS,
  computeCtmFields,
  type CtmFieldEndpoint,
} from "./fields";

// PII + CSV-injection helpers
export {
  CTM_PII_FIELDS,
  CTM_PII_FIELDS_CAMEL,
  CTM_WEBVISIT_PII_FIELDS,
  stripCsvInjectionPrefix,
} from "./pii";

// Transformers
export {
  transformCtmCallRow,
  transformCtmFormSubmissionRow,
  transformCtmSmsRow,
  // Story 35.4 — NEW transformers
  transformCtmTriggerRow,
  transformCtmIntegrationRow,
  normalizeCtmTimestampToUtc,
  type CtmReportType,
  type CtmCallApiRow,
  type CtmFormSubmissionApiRow,
  type CtmSmsApiRow,
  type CtmTriggerApiRow,
  type CtmIntegrationApiRow,
} from "./transformers";

// Pagination
export {
  walkCtmPagination,
  CTM_PAGINATION_MAX_PAGES,
  type PaginationPageResult,
  type PaginationWalkResult,
} from "./pagination";
